import { signal, effect } from '@preact/signals';
import type { Signal } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

export type BadgeId =
  | 'first-chapter'
  | 'whole-book'
  | 'nt-complete'
  | 'ot-complete'
  | 'whole-bible'
  | 'streak-7'
  | 'streak-30'
  | 'streak-100'
  | 'bookmark-hoarder'
  | 'highlighter'
  | 'spelunker'
  | 'five-translations'
  | 'memorizer';

export type BadgeMeta = {
  id: BadgeId;
  label: string;
  description: string;
  icon: string;
};

export type EarnedEntry = { earnedAt: number; shared: boolean };

const STORAGE_KEY = '_bg1';

export const BADGE_CATALOG: BadgeMeta[] = [
  { id: 'first-chapter',     label: 'First Chapter',     description: 'Listen to your first complete chapter',          icon: '📖' },
  { id: 'whole-book',        label: 'Whole Book',         description: 'Hear every chapter of any single book',          icon: '📕' },
  { id: 'nt-complete',       label: 'NT Complete',        description: 'Finish every New Testament chapter',             icon: '✝️' },
  { id: 'ot-complete',       label: 'OT Complete',        description: 'Finish every Old Testament chapter',             icon: '📜' },
  { id: 'whole-bible',       label: 'Whole Bible',        description: 'Listen through the entire Bible',                icon: '🌟' },
  { id: 'streak-7',          label: '7-Day Streak',       description: 'Listen 7 days in a row',                         icon: '🔥' },
  { id: 'streak-30',         label: '30-Day Streak',      description: 'Listen 30 days in a row',                        icon: '💎' },
  { id: 'streak-100',        label: 'Century',            description: 'Listen 100 days in a row',                       icon: '👑' },
  { id: 'bookmark-hoarder',  label: 'Bookmark Hoarder',   description: 'Save 50 bookmarks',                              icon: '🔖' },
  { id: 'highlighter',       label: 'Highlighter',        description: 'Highlight 20 verses',                            icon: '🖍️' },
  { id: 'spelunker',         label: 'Spelunker',          description: 'Jump to 25 Atlas cross-references',              icon: '🧭' },
  { id: 'five-translations', label: 'Polyglot',           description: 'Listen in 5 different translations',             icon: '🌍' },
  { id: 'memorizer',         label: 'Memorizer',          description: 'Review 5 verse cards to completion',             icon: '🧠' },
];

function load(): Record<string, EarnedEntry> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export const earnedBadges: Signal<Record<string, EarnedEntry>> = signal<Record<string, EarnedEntry>>(load());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(earnedBadges.value));
  });
}

export function earnBadge(id: BadgeId): boolean {
  const current = earnedBadges.peek();
  if (current[id]) return false;
  earnedBadges.value = { ...current, [id]: { earnedAt: Date.now(), shared: false } };
  return true;
}

export function markBadgeShared(id: BadgeId): void {
  const current = earnedBadges.peek();
  if (!current[id]) return;
  earnedBadges.value = { ...current, [id]: { ...current[id]!, shared: true } };
}

export function isEarned(id: BadgeId): boolean {
  return !!earnedBadges.value[id];
}
